/* eslint-disable react/no-danger */
// Third Party Imports
import React from 'react';

// HomeAway Imports
import {SVGIcon} from '@homeaway/react-svg';
import {Button, ButtonPrimary} from '@homeaway/react-buttons';
import {FormInput, RadioButton, RadioGroup} from '@homeaway/react-form-components';
import FormSelect from '@homeaway/react-form-select';
import InputTel from '@homeaway/react-input-tel';
import Tooltip from '@homeaway/react-tooltip';
import {isEmpty} from 'lodash';
import {InlineMessage} from '@homeaway/react-alerts';

// Relative Imports
import DialogBox from '../DialogBox';
import AlertBox from '../Alert/Alert';
import EditForm from './EditForm';
import backgroundImage1 from '../../images/intro_img1.svg';
import backgroundImage2 from '../../images/intro_img2.svg';
import './ReviewPage.less';
import accountIcon from '../../images/account-icon';
import vatIcon from '../../images/vat_icon.svg';
import toolTipInfoIconGray from '../../images/toolTip_info_icon_gray.svg';
import toolTipInfoIconBlue from '../../images/toolTip_info_icon_blue.svg';
import {
    handleFormFields,
    handleFormFieldsOnBlur,
    addressFormErrorMapper,
    addressFormValuesMapper,
    getSelectOptions
} from '../utils/addressUtil';
import Address from '../Address/Address';
import {getTaxIdentifierLabel, processTranslation} from '../utils/commonUtil';
import {VatChoice, RegistrationNumber} from '../utils/vatUtil';
import {ADDRESS_TYPE_BILLING} from "../../utils/constants";
import {isPortugueseSite, excludeTaxIdentifier, isValidTaxIdentifierCountry} from "../../utils/validate";

export default class ReviewPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {isHovered: false};
        this.toggleIconHover = this.toggleIconHover.bind(this);
    }

    componentDidMount() {
        this.props.triggerEdapPageViewAction('PPM Account: Review Page');
    }

    setAddressFormValues = (addressType) => {
        return addressFormValuesMapper({
            addressLine1Val: this.props[`${addressType}AddressLine1`],
            addressLine2Val: this.props[`${addressType}AddressLine2`],
            cityVal: this.props[`${addressType}City`],
            stateVal: this.props[`${addressType}State`],
            stateSelectVal: this.props[`${addressType}State`],
            postalCodeVal: this.props[`${addressType}PostalCode`],
            translations: this.props.translations,
            addressType
        });
    }

    setAddressFormErrors = (addressType) => {
        return addressFormErrorMapper({
            countryError: this.props[`${addressType}CountryError`],
            addressLine1Error: this.props[`${addressType}AddressLine1Error`],
            cityError: this.props[`${addressType}CityError`],
            stateError: this.props[`${addressType}StateError`],
            postalCodeError: this.props[`${addressType}PostalCodeError`],
            translations: this.props.translations,
            addressType
        });
    }

    toggleIconHover() {
        this.setState({isHovered: !this.state.isHovered});
    }

    getHeaderClass = () => (this.props.siteName === 'homeaway_de' || this.props.siteName === 'homeaway_it' || this.props.siteName === 'abritel') ? 'head-expanded' : 'head';

    getEditButtonClass = () => (this.props.siteName === 'homeaway_de' || this.props.siteName === 'homeaway_it' || this.props.siteName === 'abritel') ? 'edit-btn-expanded' : 'edit-btn';

    getDialogHeading(headerKeys, iconHtml) {
        const {translations, editOnClickHandler} = this.props;

        return (
            <div className="dialog-header">
                <div className="header-wrapper">
                    <div className="header-icon">{iconHtml}</div>
                    <div className={this.getHeaderClass()}>
                        <h2>{translations[headerKeys.head]}</h2>
                        <Tooltip
                            content={<span dangerouslySetInnerHTML={{__html: translations[headerKeys.popup]}}/>}
                            placement="bottom"
                            portalClassName="tooltip-content"
                        >
                            <img className="tool-tip-icon" src={(this.state.isHovered) ? toolTipInfoIconBlue() : toolTipInfoIconGray()} onMouseEnter={this.toggleIconHover} onMouseLeave={this.toggleIconHover}/>
                        </Tooltip>
                    </div>
                    <div className={this.getEditButtonClass()}>
                        <Button
                            id={headerKeys.id}
                            label={translations[headerKeys.button]}
                            onClick={editOnClickHandler}
                            disabled={headerKeys.btnDisabled}
                        />
                    </div>
                </div>
            </div>
        );
    }

    isAddressField = (key) => (key === 'ipmob.ppm.review_page.label_5' || key === 'ipmob.ppm.review_page.label_4' || key === 'ipmob.ppm.review_page.label_6');

    isEmptyField = (fieldVal) => (!fieldVal || fieldVal === '–');

    getInlineMessage = () => {
        const {translations} = this.props;

        return (<div className="inline-message">
            <InlineMessage
                id="inlineMessage"
                type="danger"
                message={`${translations['ipmob.input.field.required']}`}
            />
        </div>);
    }

    getAddressField = (dialogContentInfo, key) => {
        const {translations} = this.props;
        const addressList = dialogContentInfo[key];
        const response = [];
        const fieldVal = addressList[0];

        if (this.isEmptyField(fieldVal)) {
            response.push(<h4 key={key} className="dialog-sub-head">{translations[key]}</h4>);
            response.push(this.getInlineMessage());
        } else {
            response.push(<h4 key={key} className="dialog-sub-head-expanded">{translations[key]}</h4>);
        }

        for (const [i, address] of addressList.entries()) {
            if (address) {
                response.push(<span key={i} className="dialog-desc">{address}</span>);
            }
        }

        return response;
    }

    getField = (dialogContentInfo, key) => {
        const {translations} = this.props;
        const fieldVal = dialogContentInfo[key];

        return (
            <div>
                <h4 className={`${key === 'ipmob.company_location.label_2' ? 'dialog-sub-head-expanded' : 'dialog-sub-head'}`}>{translations[key]}</h4>
                {
                    (this.isEmptyField(fieldVal)) ? this.getInlineMessage() : null
                }
                <span className="dialog-desc">{fieldVal}</span>
            </div>
        );
    }

    processContent(dialogContentInfo) {
        const responseHtml = [];

        for (const key in dialogContentInfo) {
            if (Object.prototype.hasOwnProperty.call(dialogContentInfo, key)) {
                responseHtml.push(<div key={key} className="dialog-content">
                    {
                        (this.isAddressField(key) && dialogContentInfo[key] && dialogContentInfo[key].length > 0)
                            ? this.getAddressField(dialogContentInfo, key)
                            : this.getField(dialogContentInfo, key)
                    }
                </div>);
            }
        }

        return responseHtml;
    }

    getAccountContents() {
        const {translations, accountDetails} = this.props;
        const accountHeaderKeys = {
            head: 'ipmob.ppm.review_page.sub_head_1',
            button: 'ipmob.ppm.review_page.button.text',
            popup: 'ipmob.ppm.review_page.popup_1',
            id: 'accountInfoEdit',
            btnDisabled: isEmpty(accountDetails)
        };
        const accountContentInfo = {
            'ipmob.billing_page.label_1': accountDetails.name,
            'ipmob.ppm.review_page.label_1': accountDetails.primaryEmail,
            'ipmob.ppm.review_page.label_2': accountDetails.primaryPhone,
            'ipmob.create_account.label_2': accountDetails.companyName,
            'ipmob.ineligible_help.label_1': accountDetails.companyLocation,
            'ipmob.ppm.review_page.label_5': accountDetails.mailingAddress,
            'ipmob.ppm.review_page.label_6': accountDetails.billingAddress
        };
        const iconHtml = (<SVGIcon markup={accountIcon}/>);
        const dialogHeading = this.getDialogHeading(accountHeaderKeys, iconHtml);
        const dialogContent = this.processContent(accountContentInfo);

        return {
            dialogHeading,
            heading: translations['ipmob.ppm.review_page.head'],
            headingDesc: translations['ipmob.ppm.review_page.head_desc'],
            contents: dialogContent,
            styles: {
                width: 576,
                height: 502,
                innerPadding: 48,
                outerPaddingTop: 10,
                outerPaddingBottom: 0
            }
        };
    }

    getRegistrationContentInfo() {
        let {registrationDetails, companyCountryCode} = this.props;
        let registrationContentInfo;
        switch (companyCountryCode) {
            case 'FR':
                registrationContentInfo = {
                    'ipmob.mailing_address.label_1': registrationDetails.countryCode,
                    'ipmob.company_location.label_4': registrationDetails.registrationNumber
                };
                break;
            case 'DE':
                registrationContentInfo = {
                    'ipmob.mailing_address.label_1': registrationDetails.countryCode,
                    'ipmob.company_location.label_5': registrationDetails.registrationNumber
                };
                break;
            case 'PT':
                registrationContentInfo = {
                    'ipmob.mailing_address.label_1': registrationDetails.countryCode,
                    'ipmob.company_location.label_6': registrationDetails.registrationNumber
                };
                break;
            case 'ES':
                registrationContentInfo = {
                    'ipmob.mailing_address.label_1': registrationDetails.countryCode,
                    'ipmob.company_location.label_7': registrationDetails.registrationNumber
                };
                break;
            case 'GB':
                registrationContentInfo = {
                    'ipmob.mailing_address.label_1': registrationDetails.countryCode,
                    'ipmob.company_location.label_3': registrationDetails.registrationNumber
                };
                break;
            default:
                registrationContentInfo = {
                    'ipmob.mailing_address.label_1': registrationDetails.countryCode,
                    'ipmob.company_location.label_2': registrationDetails.registrationNumber
                };
                break;
        }

        return registrationContentInfo;
    }

    getVatContents() {
        let {vatDetails, registrationDetails, showRegistrationNumber, translations, companyLocation, enableVatEditButton} = this.props;
        const vatHeaderKeys = {
            head: 'ipmob.ppm.review_page.sub_head_2',
            button: 'ipmob.ppm.review_page.button.text',
            popup: 'ipmob.vat_page.alert_box.content_1',
            id: 'vatInfoEdit',
            btnDisabled: !enableVatEditButton
        };
        companyLocation = companyLocation || 'Europe';

        let vatContentInfo;
        let dialogContent;
        if (showRegistrationNumber) {
            if (registrationDetails.registrationNumber) {
                vatHeaderKeys.head = 'ipmob.ppm.review_page.sub_head_3';
                vatHeaderKeys.popup = 'ipmob.ppm.review_page.alert_box.content';
                vatContentInfo = this.getRegistrationContentInfo();
                dialogContent = this.processContent(vatContentInfo);
            } else {
                dialogContent = <div>{processTranslation(translations['ipmob.ppm.review_page.desc'], [companyLocation])}</div>;
            }
        } else {
            vatContentInfo = {
                'ipmob.mailing_address.label_1': vatDetails.countryCode,
                'ipmob.vat_page.label_2': vatDetails.vatNumber
            };
            if (!this.isEmptyField(vatDetails.regionCode)) {
                vatContentInfo['ipmob.vat_page.label_1'] = vatDetails.regionCode;
            }
            vatContentInfo['ipmob.ppm.review_page.label_4'] = vatDetails.vatAddress;
            dialogContent = this.processContent(vatContentInfo);
        }

        const iconHtml = (<img src={vatIcon()}/>);
        const dialogHeading = this.getDialogHeading(vatHeaderKeys, iconHtml);

        return {
            dialogHeading,
            contents: dialogContent,
            styles: {
                width: 576,
                height: 200,
                innerPadding: 48,
                outerPaddingTop: 0,
                outerPaddingBottom: 10
            }
        };
    }

    getAccountInfoDialogBox() {
        const {userType} = this.props;
        const accountReviewContents = this.getAccountContents();

        return (
            <DialogBox
                styles={accountReviewContents.styles}
                contents={accountReviewContents.contents}
                dialogHeading={accountReviewContents.dialogHeading}
                heading={accountReviewContents.heading}
                headingDesc={accountReviewContents.headingDesc}
                userType={userType}
            />
        );
    }

    getVatInfoDialogBox() {
        const {userType} = this.props;
        const vatReviewContents = this.getVatContents();

        return (
            <DialogBox
                styles={vatReviewContents.styles}
                contents={vatReviewContents.contents}
                dialogHeading={vatReviewContents.dialogHeading}
                userType={userType}
            />
        );
    }

    handleFields = (event) => {
        return handleFormFields(event, this.props);
    };

    handleFieldsBlur = (event) => {
        return handleFormFieldsOnBlur(event, this.props);
    };

    getAddress() {
        const {
            translations,
            countryList,
            addressLine1Error,
            cityError,
            stateError,
            addressCountryError,
            postalCodeError,
            city,
            state,
            addressLine1,
            addressLine2,
            addressCountry,
            postalCode
        } = this.props;

        const countryOptions = getSelectOptions(countryList);

        return (
            <div className="address-holder">
                {
                    <Address
                        translations={translations}
                        countryList={countryOptions}
                        country={addressCountry}
                        addressFormValues={addressFormValuesMapper({
                            addressLine1Val: addressLine1,
                            addressLine2Val: addressLine2,
                            cityVal: city,
                            stateVal: state,
                            postalCodeVal: postalCode
                        })}
                        addressFormErrors={addressFormErrorMapper({
                            translations,
                            postalCodeError,
                            addressLine1Error,
                            cityError,
                            stateError,
                            StateSelectError: stateError,
                            countryError: addressCountryError
                        })}
                        handleFieldsOnBlur={this.handleFieldsBlur}
                        handleAddressFields={this.handleFields}
                    />
                }
            </div>
        );
    }

    getBillingAddress() {
        const {
            translations,
            countryList,
            billingCountry
        } = this.props;

        const countryOptions = getSelectOptions(countryList);

        return (
            <div className="address-holder">
                {
                    <Address
                        translations={translations}
                        countryList={countryOptions}
                        country={billingCountry}
                        addressFormValues={this.setAddressFormValues(ADDRESS_TYPE_BILLING)}
                        addressFormErrors={this.setAddressFormErrors(ADDRESS_TYPE_BILLING)}
                        handleFieldsOnBlur={this.handleFieldsBlur}
                        handleAddressFields={this.handleFields}
                        addressType={ADDRESS_TYPE_BILLING}
                    />
                }
            </div>
        );
    }

    getEditAccountFormContents() {
        const {
            translations,
            editFormOnchangeHandler,
            editFormOnBlurHandler,
            handlePhoneNumberChange,
            handleCountryCodeChange,

            firstName,
            lastName,
            emailAddress,
            phoneNumber,
            dialingCountryCode,
            companyName,
            companyCountryCode,
            countryList,

            firstNameError,
            lastNameError,
            phoneNumberError,
            companyNameError,
            companyCountryCodeError,

            editModalServiceError,
            handleModalAlertDismiss,
            getInputTel,
            handleChoiceChange,
            choice,
            accountDetails,
            siteName
        } = this.props;

        const countryOptions = getSelectOptions(countryList);
        const isBillingAddressPresent = (accountDetails.billingCountry);

        let extendedStyle = '';
        if (isPortugueseSite(siteName)) {
            extendedStyle = '-extended';
        }

        return (
            <div className="account-edit-form">
                {
                    (editModalServiceError)
                        ? <div className="modal-alert-panel">
                            <AlertBox
                                messageKey={editModalServiceError}
                                translations={translations}
                                handleAlertDismiss={handleModalAlertDismiss}
                                size={'sm'}
                            />
                        </div>
                        : null
                }
                <p className="sub-head">{translations['ipmob.ppm.review_page.popup_1']}</p>
                <div className="edit-form-contents">
                    <div>{`${translations['ipmob.ppm.review_page.label_1']}: ${emailAddress}`}</div>
                </div>
                <div className="wrapper">
                    <div className="content-col">
                        <div className="wrapper">
                            <div className="sub-content-col">
                                <FormInput
                                    id="firstName"
                                    type="text"
                                    name="First name"
                                    label={translations['ipmob.create_account.label_3']}
                                    value={firstName}
                                    maxLength="64"
                                    onChange={editFormOnchangeHandler}
                                    onBlur={editFormOnBlurHandler}
                                    errorMsg={translations[firstNameError]}
                                />
                            </div>
                            <div className="sub-content-col">
                                <FormInput
                                    id="lastName"
                                    type="text"
                                    name="Last name"
                                    label={translations['ipmob.create_account.label_4']}
                                    value={lastName}
                                    maxLength="64"
                                    onChange={editFormOnchangeHandler}
                                    onBlur={editFormOnBlurHandler}
                                    errorMsg={translations[lastNameError]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="wrapper">
                    <div className="sub-content-col phone-number">
                        <InputTel
                            ref= {getInputTel}
                            id="phoneNumber"
                            name="Phone"
                            className="phone-number"
                            labels={{phoneNumber: translations['ipmob.create_account.label_8']}}
                            phoneNumber={phoneNumber}
                            countryCode={dialingCountryCode}
                            onPhoneBlur={editFormOnBlurHandler}
                            errorMsg={translations[phoneNumberError]}
                            onPhoneChange={handlePhoneNumberChange}
                            onCountryCodeChange={handleCountryCodeChange}
                        />
                    </div>
                </div>
                <div className="wrapper">
                    <div className="content-col">
                        <FormInput
                            id="companyName"
                            type="text"
                            label={translations['ipmob.create_account.label_2']}
                            value={companyName}
                            maxLength="64"
                            onChange={editFormOnchangeHandler}
                            onBlur={editFormOnBlurHandler}
                            errorMsg={translations[companyNameError]}
                        />
                    </div>
                </div>
                <div className="wrapper">
                    <div className="content-col">
                        <FormSelect
                            id="companyCountryCode"
                            name="Country selected"
                            items={countryOptions}
                            label={translations['ipmob.ineligible_help.label_1']}
                            value={companyCountryCode}
                            onChange={editFormOnchangeHandler}
                            onBlur={editFormOnBlurHandler}
                            errorMsg={translations[companyCountryCodeError]}
                        />
                    </div>
                </div>
                <div className="edit-form-contents">
                    <div>{translations['ipmob.mailing_address.sub_heading']}</div>
                </div>
                <div className="wrapper">
                    <div className="content-col">
                        <form id="addressForm" role="form">
                            <div className="input-container" aria-label="Big form">
                                {
                                    this.getAddress()
                                }
                            </div>
                        </form>
                    </div>
                </div>
                <div className="edit-form-contents">
                    <div>{translations['ipmob.billing_page.sub_heading_3']}</div>
                </div>
                <div className="wrapper">
                    <div className={`address-choice content-col${extendedStyle}`}>
                        {
                            (!isBillingAddressPresent) &&
                            <RadioGroup name="choice" ariaLabel="Address choices for mailing and billing addresses">
                                <RadioButton
                                    id="mailingAddressChoice"
                                    label={translations['ipmob.billing_address.choice1']}
                                    value="Mailing address"
                                    onChange={handleChoiceChange}
                                    size="sm"
                                    checked={choice === 'Mailing address'}
                                    type={'radio'}
                                />
                                <RadioButton
                                    id="billingAddressChoice"
                                    label={translations['ipmob.billing_address.choice2']}
                                    value="Billing address"
                                    onChange={handleChoiceChange}
                                    size="sm"
                                    checked={choice === 'Billing address'}
                                    type={'radio'}
                                />
                            </RadioGroup>
                        }
                    </div>
                </div>
                <div className="wrapper">
                    <div className="content-col">
                        <form id="billing_addressForm" role="form">
                            <div className="input-container" aria-label="Big form">
                                {
                                    this.getBillingAddress()
                                }
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    getEditVatFormContents(styleInfo) {
        const {
            translations,
            countryList,
            vatRegionCodeOptions,

            editFormOnchangeHandler,
            editFormOnBlurHandler,

            vatCountryCode,
            vatRegionCode,
            vatNumber,

            vatCountryCodeError,
            vatNumberError,

            editModalServiceError,
            handleModalAlertDismiss
        } = this.props;

        const countryOptions = getSelectOptions(countryList);
        const vatKey = translations['ipmob.vat_page.vat_key'];
        const stringToReplace = ['', vatKey];

        return (
            <div className="vat-edit-form">
                {
                    (editModalServiceError && !styleInfo.skipSubHead)
                        ? <div className="modal-alert-panel">
                            <AlertBox
                                messageKey={editModalServiceError}
                                translations={translations}
                                handleAlertDismiss={handleModalAlertDismiss}
                                size={'sm'}
                            />
                        </div>
                        : null
                }
                {
                    (styleInfo.skipSubHead)
                        ? null
                        : <div className="sub-head">
                            <div dangerouslySetInnerHTML={{__html: translations['ipmob.vat_page.alert_box.content_1']}}/>
                            <div>{translations['ipmob.vat_page.alert_box.content_2']}</div>
                        </div>
                }
                <div className="wrapper">
                    <div className="content-col">
                        <FormSelect
                            id="vatCountry"
                            name="Country selected - VAT"
                            items={countryOptions}
                            label={translations['ipmob.address.label_1']}
                            value={vatCountryCode}
                            onChange={editFormOnchangeHandler}
                            errorMsg={translations[vatCountryCodeError]}
                            onBlur={editFormOnBlurHandler}
                        />
                    </div>
                </div>
                {
                    (vatRegionCodeOptions.length !== 0)
                        ? <div className="wrapper">
                            <div className="content-col">
                                <FormSelect
                                    id="vatRegionCode"
                                    name="Region code selected - VAT"
                                    items={vatRegionCodeOptions}
                                    label={translations['ipmob.vat_page.label_1']}
                                    value={vatRegionCode}
                                    onChange={editFormOnchangeHandler}
                                    onBlur={editFormOnBlurHandler}
                                />
                            </div>
                        </div>
                        : null
                }
                <div className="wrapper">
                    <div className="content-col">
                        <FormInput
                            id="vatNumber"
                            type="text"
                            name="VAT Number"
                            label={translations['ipmob.vat_page.label_2']}
                            value={vatNumber}
                            maxLength="64"
                            onChange={editFormOnchangeHandler}
                            errorMsg={translations[vatNumberError]}
                            onBlur={editFormOnBlurHandler}
                        />
                    </div>
                </div>
                <div className="wrapper">
                    <div className="content-col">
                        <form id="addressForm" role="form">
                            <div className="input-container" aria-label="Big form">
                                <div className="wrapper">
                                    <div className="content-col">
                                        <h3>{processTranslation(translations['ipmob.vat_page.sub_heading_2'], stringToReplace)}</h3>
                                    </div>
                                </div>
                                {
                                    this.getAddress()
                                }
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    getRegistrationFormContents() {
        const {
            translations,
            countryList,
            editModalServiceError,
            handleModalAlertDismiss,
            editFormOnchangeHandler,
            editFormOnBlurHandler,
            registrationNumber,
            vatCountryCode,
            registrationNumberError,
            vatCountryCodeError,
            companyCountryCode
        } = this.props;

        const countryOptions = getSelectOptions(countryList);
        const noteLabel = translations['ipmob.company_location.note_label'];
        const taxIdentifierLabel = isValidTaxIdentifierCountry(companyCountryCode) ? translations[getTaxIdentifierLabel(companyCountryCode)] : translations['ipmob.company_location.label_2'];

        return (<div className="vat-edit-form">
            {
                (editModalServiceError)
                    ? <div className="modal-alert-panel">
                        <AlertBox
                            messageKey={editModalServiceError}
                            translations={translations}
                            handleAlertDismiss={handleModalAlertDismiss}
                            size={'sm'}
                        />
                    </div>
                    : null
            }
            <div className="sub-head">
                <div>{translations['ipmob.ppm.review_page.alert_box.content']}</div>
            </div>

            <div className="wrapper">
                <div className="content-col">
                    <FormSelect
                        id="vatCountry"
                        name="Country selected - VAT"
                        items={countryOptions}
                        label={translations['ipmob.address.label_1']}
                        value={vatCountryCode}
                        onChange={editFormOnchangeHandler}
                        errorMsg={translations[vatCountryCodeError]}
                        onBlur={editFormOnBlurHandler}
                    />
                </div>
            </div>
            {(companyCountryCode === 'GB')?
                <div className="wrapper">
                    <div className="content-col">
                        <text>{noteLabel}</text>
                    </div>
                </div>
                :null
            }
            <div className="wrapper">
                <div className="content-col">
                    <FormInput
                        id="registrationNumber"
                        type="text"
                        name="Registration Number"
                        label={taxIdentifierLabel}
                        value={registrationNumber}
                        maxLength="64"
                        onChange={editFormOnchangeHandler}
                        errorMsg={translations[registrationNumberError]}
                        onBlur={editFormOnBlurHandler}
                    />
                </div>
            </div>
        </div>);
    }

    getVatChoiceFormContents() {
        const {editModalServiceError, translations, handleModalAlertDismiss, vatChoice, companyCountryCode} = this.props;

        return (<div className="vat-edit-form">
            {
                (editModalServiceError)
                    ? <div className="modal-alert-panel">
                        <AlertBox
                            messageKey={editModalServiceError}
                            translations={translations}
                            handleAlertDismiss={handleModalAlertDismiss}
                            size={'sm'}
                        />
                    </div>
                    : null
            }

            <div className="sub-head">
                <div dangerouslySetInnerHTML={{__html: translations['ipmob.vat_page.alert_box.content_1']}}/>
                <div>{translations['ipmob.vat_page.alert_box.content_2']}</div>
            </div>

            {<VatChoice {...this.props} />}
            {(vatChoice === 'Yes') ? this.getEditVatFormContents({skipSubHead: true}) : this.getTaxIdentifierInfo(companyCountryCode)}

        </div>);
    }

    getTaxIdentifierInfo(companyCountryCode) {
        return ((!excludeTaxIdentifier(companyCountryCode)) ? <RegistrationNumber {...this.props} /> : null);
    }

    getEditFormFooter(footerInfo) {
        const {translations, handleEditModalDismiss, handleEditModalSubmit, enableSaveButton} = this.props;

        return (
            <div className="wrapper">
                <div className="edit-modal-btn">
                    <Button
                        type="reset"
                        id={footerInfo.cancelBtnId}
                        className="cancel-btn"
                        label={translations['ipmob.ineligible_help.cancel']}
                        onClick={handleEditModalDismiss}
                        size="sm"
                    />
                    <ButtonPrimary
                        type="submit"
                        id={footerInfo.submitBtnId}
                        className="save-btn"
                        label={translations['ipmob.review.save']}
                        size="sm"
                        onClick={handleEditModalSubmit}
                        disabled={!enableSaveButton}
                    />
                </div>
            </div>
        );
    }

    getEditAccountInfo() {
        return ({
            editFormHead: this.props.translations['ipmob.ppm.review_page.sub_head_1'],
            editFormBody: this.getEditAccountFormContents(),
            editFormFooter: this.getEditFormFooter({cancelBtnId: 'accountCancel', submitBtnId: 'accountSubmit'})
        });
    }

    getEditVatInfo() {
        const {showRegistrationNumber, translations, registrationDetails} = this.props;
        let editFormHead;
        let editFormBody;

        editFormHead = translations['ipmob.ppm.review_page.sub_head_2'];
        if (showRegistrationNumber) {
            if (registrationDetails.registrationNumber) {
                editFormHead = translations['ipmob.ppm.review_page.sub_head_3'];
                editFormBody = this.getRegistrationFormContents();
            } else {
                editFormBody = this.getVatChoiceFormContents();
            }
        } else {
            editFormBody = this.getEditVatFormContents({skipSubHead: false});
        }

        return ({
            editFormHead,
            editFormBody,
            editFormFooter: this.getEditFormFooter({cancelBtnId: 'vatCancel', submitBtnId: 'vatSubmit'})
        });
    }

    isRegistrationModalOpen = () => (this.props.enableVatModal && this.props.registrationDetails.registrationNumber);

    render() {
        const {
            handleContinueSubmit,
            translations,
            serviceError,
            handleAlertDismiss,
            vatDetails,
            enableAccountModal,
            enableVatModal,
            incompleteForm,
            handleEditModalDismiss,
            enableLoadOverlay,
            showVatChoice,
            editModalServiceError,
            scrollModalToTop
        } = this.props;

        let editReviewDetails = {};
        if (enableAccountModal) {
            editReviewDetails = this.getEditAccountInfo();
        } else if (enableVatModal) {
            editReviewDetails = this.getEditVatInfo();
        }

        const enableModal = (enableAccountModal || enableVatModal);

        return (
            <div id="reviewPage" className="review-page">
                {
                    (serviceError)
                        ? <div className="alert-container">
                            <div className="alert-wrapper">
                                <div className="alert-panel">
                                    <AlertBox
                                        messageKey={serviceError}
                                        translations={translations}
                                        handleAlertDismiss={handleAlertDismiss}
                                    />
                                </div>
                            </div>
                        </div>
                        : null
                }
                {
                    (enableModal)
                        ? <div id="editForm" className={`${this.isRegistrationModalOpen() ? 'reg-edit-form' : 'edit-form'}`}>
                            <EditForm
                                handleOnClose={handleEditModalDismiss}
                                editModalHead={editReviewDetails.editFormHead}
                                editModalBody={editReviewDetails.editFormBody}
                                editModalFooter={editReviewDetails.editFormFooter}
                                showModal={enableModal}
                                isScrollTop={!!editModalServiceError}
                                scrollModalToTop={scrollModalToTop}
                            />
                        </div>
                        : null
                }
                <div className="review-page-wrapper">
                    <div className="overlay"/>
                    <img className="review-pg-img review-pg-img-1" src={backgroundImage1()}/>
                    <img className="review-pg-img review-pg-img-2" src={backgroundImage2()}/>
                    {this.getAccountInfoDialogBox()}
                    {
                        !isEmpty(vatDetails) || showVatChoice ? this.getVatInfoDialogBox() : null
                    }
                    <div className="review-btn" >
                        <ButtonPrimary
                            label={translations['ipmob.create_account.button.text_2']}
                            type="submit"
                            value="Submit"
                            id="reviewBtn"
                            onClick={handleContinueSubmit}
                            disabled={incompleteForm || enableLoadOverlay}
                        />
                    </div>
                    <div className="review-footer">
                        <span>{translations['ipmob.introduction.description_5']}</span>
                    </div>
                </div>
                <div className="trim-bg-image"/>
            </div>
        );
    }
}
